import { Grid, Stack, TextField } from "@mui/material";
import sortBy from "lodash/sortBy";
import type { FC } from "react";
import { useCallback, useRef } from "react";
import type { UserRole } from "../../auth/auth";
import DatePicker from "../../components/datePicker/DatePickerV2";
import { InfoEdit } from "../../components/editDialog/InfoEdit";
import LabelledSwitch from "../../components/labelledSwitch/LabelledSwitch";
import NumberField from "../../components/numberField/NumberField";
import TagSelect from "../../components/tagSelect/TagSelect";
import { OrderStatus } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { removeArr, updateArr } from "../../misc/utils";
import PackageSelect from "../../sharedComponents/packages/PackageSelect";
import RegionSelect from "../../sharedComponents/regions/RegionSelect";
import OrderInvoices from "../invoices/OrderInvoices";
import Confirmations from "../orderConfirmation/Confirmations";
import { OrderStatusSelect } from "../orderStatus/OrderStatusSelect";
import type { OrderProduct, SavedOrder, SavedOrderProduct } from "../orderTypes";
import type { PaymentDTO } from "../ordersApi";
import { useTripTagsByCategory } from "../useTripTagsByCategory";
import CustomerAndContactSelect from "./CustomerAndContactSelect";
import GroupTypeSelect from "./GroupTypeSelect";
import OrderCurrency from "./OrderCurrency";
import OrderPayments from "./OrderPayments";
import SupervisorSelect from "./SupervisorSelect";
import { AssociatedFilesEdit } from "./attachments/AssociatedFilesEdit";
import { useAttachments } from "./attachments/useAttachments";
import CancellationConditionSelect from "./CancellationConditionSelect";
import { getTotalPrice } from "./getTotalPrice";
import { useOrderToProducts } from "./orderToProducts";
import OrderProductsTable from "./productsTable/OrderProductsTable";
import { useOrderDefaultCancellationDate } from "./useOrderDefaultCancellationDate";

type Order = Omit<
  Optional<SavedOrder, "id" | "created" | "invoices" | "isHistorical" | "payments" | "userFriendlyId">,
  "products"
> & {
  readonly products: (OrderProduct | SavedOrderProduct)[];
};

function isSavedOrder(order: Pick<Order | SavedOrder, "id">): order is SavedOrder {
  return !!order.id;
}

interface OrderFilesProps {
  readonly order: Pick<Order | SavedOrder, "id" | "files">;
  readonly onAddAttachment: (formData: FormData) => Promise<void>;
  readonly onRemoveAttachment: (idx: number) => Promise<void>;
}

const OrderFiles: FC<OrderFilesProps> = ({ order, onAddAttachment, onRemoveAttachment }) => {
  if (!isSavedOrder(order)) {
    return null;
  }
  return (
    <Grid size={12} data-testid="order-attachments">
      <Stack direction="column" spacing={1}>
        <AssociatedFilesEdit values={order.files} onAdd={onAddAttachment} onDelete={onRemoveAttachment} />
        <OrderInvoices order={order} />
        <Confirmations orderId={order.id} />
      </Stack>
    </Grid>
  );
};

interface InfoForSupervisorProps {
  readonly order: Pick<Order, "infoForSupervisor">;
  readonly onChange: (change: Pick<Order, "infoForSupervisor">) => void;
}

const InfoForSupervisor: FC<InfoForSupervisorProps> = ({ order, onChange }) => {
  return (
    <Grid size={{ xs: 12, lg: 6 }}>
      <InfoEdit
        label="Info dla opiekuna"
        value={order.infoForSupervisor}
        onChange={(infoForSupervisor) => onChange({ infoForSupervisor })}
      />
    </Grid>
  );
};

interface Props {
  readonly order: Order;
  readonly role: Exclude<UserRole, "CUSTOMER">;
  readonly onChange: (order: Partial<Order>) => void;
  readonly onUpdatePayments?: (payments: PaymentDTO[]) => Promise<void>;
  readonly onCancelProduct?: (productIndexes: number[], bookingNumber: string, cancelledAt: string) => Promise<void>;
  /** Undoing a cancellation is admin-only, so the handler is absent for every other role. */
  readonly onRestoreProduct?: (productIndexes: number[], bookingNumber: string) => Promise<void>;
  readonly onMoveProduct?: (productIndexes: number[], targetOrderId: string) => Promise<void>;
}

const OrderEdit: FC<Props> = ({
  order,
  role,
  onChange,
  onUpdatePayments,
  onCancelProduct,
  onRestoreProduct,
  onMoveProduct,
}) => {
  const orderRef = useRef(order);
  orderRef.current = order;

  const { addAttachment, deleteAttachment } = useAttachments(order.id!);
  const { tripTagsByCategory } = useTripTagsByCategory();
  const { products } = useOrderToProducts(order);
  const totalPrice = getTotalPrice(products);

  const effectiveCancellationDate = useOrderDefaultCancellationDate(order);

  const handleProductChange = useCallback(
    (product: OrderProduct, idx: number) => {
      onChange({
        products: updateArr(orderRef.current.products, idx, product),
      });
    },
    [onChange],
  );

  const handleProductDelete = useCallback(
    (idx: number) => {
      onChange({ products: removeArr(orderRef.current.products, idx) });
    },
    [onChange],
  );

  const handleAddFromPackage = useCallback(
    (newProducts: OrderProduct[], tags: { readonly category: string; readonly value: string }[]) => {
      onChange({
        products: sortBy(newProducts, (p) => dayjs(p.date, ISO_DATE_FORMAT).valueOf()),
        tags,
      });
    },
    [onChange],
  );

  const handleCurrencyOrExchangeRateChange = (change: Partial<Pick<Order, "currency" | "currencyExchangeRate">>) => {
    onChange({
      ...change,
      products: order.products.map((p) => ({
        ...p,
        options: p.options.map((o) => ({ id: o.id, quantity: o.quantity })),
      })) as (OrderProduct | SavedOrderProduct)[],
    });
  };

  const handleAddAttachment = async (formData: FormData) => {
    const result = await addAttachment(formData);
    onChange({ files: [...order.files, result] });
  };

  const handleRemoveAttachment = async (idx: number) => {
    const file = order.files[idx];
    const result = await deleteAttachment(file.name);
    onChange({ files: result });
  };

  const handleCancellationConditionChange = useCallback(
    (cancellationConditionId: string) => {
      onChange({ cancellationConditionId });
    },
    [onChange],
  );

  const handleSupervisorChange = (agentId: string | undefined) => {
    onChange({ supervisorId: agentId });
  };

  return (
    <Grid container size={12} spacing={2} data-testid="order-form">
      <Grid container size={{ lg: 8, xs: 12 }} spacing={2}>
        <Grid size={8}>
          <TextField
            label="Nazwa grupy"
            value={order.name}
            fullWidth
            autoFocus
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Grid>
        <Grid size={2}>
          <NumberField
            label="Liczba osób płatnych"
            fullWidth
            value={order.participantsNumber.numberOfPaid}
            min={1}
            max={1000}
            onChange={(numberOfPaid) =>
              onChange({
                participantsNumber: {
                  ...order.participantsNumber,
                  numberOfPaid: numberOfPaid ?? 0,
                },
              })
            }
          />
        </Grid>
        <Grid size={2}>
          <NumberField
            label="Liczba osób obsługi"
            fullWidth
            value={order.participantsNumber.numberOfStaff}
            min={0}
            max={1000}
            onChange={(numberOfStaff) =>
              onChange({
                participantsNumber: {
                  ...order.participantsNumber,
                  numberOfStaff: numberOfStaff ?? 0,
                },
              })
            }
          />
        </Grid>
        <Grid size={{ lg: 6, xs: 12 }}>
          <OrderStatusSelect value={order.status} role={role} onChange={(status) => onChange({ status })} />
        </Grid>
        <Grid size={{ lg: 3, xs: 6 }}>
          <DatePicker
            label="Data kontaktu"
            value={order.contactDate ? dayjs(order.contactDate, ISO_DATE_FORMAT) : null}
            fullWidth
            onChange={(contactDate) => onChange({ contactDate: contactDate?.format(ISO_DATE_FORMAT) })}
          />
        </Grid>
        <Grid size={{ lg: 3, xs: 6 }}>
          <DatePicker
            label="Data anulacji"
            value={effectiveCancellationDate ? dayjs(effectiveCancellationDate, ISO_DATE_FORMAT) : null}
            fullWidth
            onChange={(cancellationDate) => onChange({ cancellationDate: cancellationDate?.format(ISO_DATE_FORMAT) })}
          />
        </Grid>
        <CustomerAndContactSelect
          customerId={order.customerId}
          contactPersonEmail={order.customerContactPersonEmail}
          onCustomerSelect={(customerId) => onChange({ customerId })}
          onContactPersonSelect={(email) => onChange({ customerContactPersonEmail: email })}
        />
        <Grid size={6}>
          <SupervisorSelect
            value={order.supervisorId}
            customerId={order.customerId}
            onChange={handleSupervisorChange}
          />
        </Grid>
        <Grid size={6}>
          <CancellationConditionSelect
            value={order.cancellationConditionId}
            onChange={handleCancellationConditionChange}
          />
        </Grid>
        <Grid size={6} />
        <OrderFiles order={order} onAddAttachment={handleAddAttachment} onRemoveAttachment={handleRemoveAttachment} />
        <Grid size={3}>
          <GroupTypeSelect value={order.groupType} onChange={(groupType) => onChange({ groupType })} />
        </Grid>
        <Grid size={9}>
          <TagSelect tags={order.tags} availableTags={tripTagsByCategory} onChange={(tags) => onChange({ tags })} />
        </Grid>
      </Grid>
      <Grid container size={{ lg: 4, xs: 12 }} spacing={2}>
        <Grid size={12}>
          <LabelledSwitch
            label="Cena pakietowa"
            checked={order.type === "PACKAGE"}
            onChange={(v) => onChange({ type: v ? "PACKAGE" : "DEFAULT" })}
          />
        </Grid>
        <Grid container size={12}>
          <OrderCurrency
            currency={order.currency}
            exchangeRate={order.currencyExchangeRate}
            disabled={(order.invoices?.length ?? 0) > 0}
            onCurrencyChange={(currency) => handleCurrencyOrExchangeRateChange({ currency })}
            onExchangeRateChange={(er) => handleCurrencyOrExchangeRateChange({ currencyExchangeRate: er })}
          />
        </Grid>
        <Grid size={6}>
          <NumberField
            label="Kwota zaliczki"
            fullWidth
            value={order.advance?.amount}
            onChange={(amount) =>
              onChange({ advance: { amount: amount ?? undefined, deadline: order.advance.deadline } })
            }
          />
        </Grid>
        <Grid size={6}>
          <DatePicker
            label="Termin zaliczki"
            fullWidth
            value={order.advance?.deadline ? dayjs(order.advance.deadline) : null}
            onChange={(deadline) =>
              onChange({
                advance: { deadline: deadline?.format(ISO_DATE_FORMAT), amount: order.advance.amount },
              })
            }
          />
        </Grid>
        {onUpdatePayments && (
          <Grid size={12}>
            <OrderPayments
              orderId={order.id}
              toBePaid={totalPrice}
              payments={order.payments ?? []}
              currency={order.currency}
              onUpdatePayments={onUpdatePayments}
            />
          </Grid>
        )}
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <InfoEdit value={order.info} onChange={(info) => onChange({ info })} />
      </Grid>
      <InfoForSupervisor order={order} onChange={onChange} />
      <Grid size={{ xs: 12, lg: 6 }}>
        <PackageSelect
          packageId={order.packageId}
          fullWidth
          onChange={(pkg) => onChange({ packageId: pkg?.id.id, regionId: undefined })}
          disableClearable={false}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <RegionSelect
          packageId={order.packageId}
          regionId={order.regionId}
          disabled={!!order.packageId}
          fullWidth
          onChange={(region) => onChange({ regionId: region?.id.id })}
          disableClearable={false}
        />
      </Grid>
      <Grid size={12}>
        <OrderProductsTable
          orderId={order.id}
          values={products}
          currency={order.currency}
          totalPrice={totalPrice}
          isOrderHistorical={order.isHistorical ?? false}
          isOrderCancelled={order.status === OrderStatus.CANCELLED}
          onChange={handleProductChange}
          onDelete={handleProductDelete}
          onCancel={onCancelProduct}
          onRestore={onRestoreProduct}
          onMoveProduct={onMoveProduct}
          onAddFromPackage={handleAddFromPackage}
          onAddFile={handleAddAttachment}
        />
      </Grid>
    </Grid>
  );
};

export default OrderEdit;
